import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { ob, spin } from '../../onboardingV2.tokens';

const variants = {
    primary: css`
        background: ${ob.color.brand};
        color: ${ob.color.surface};
        box-shadow: ${ob.shadow.cta};
        ${ob.hover} {
            &:hover:not(:disabled):not([aria-disabled='true']) {
                background: ${ob.color.brandHover};
            }
        }
    `,
    whatsapp: css`
        background: ${ob.color.whatsapp};
        color: ${ob.color.brand};
        box-shadow: ${ob.shadow.whatsapp};
        ${ob.hover} {
            &:hover:not(:disabled):not([aria-disabled='true']) {
                background: ${ob.color.whatsappHover};
            }
        }
    `,
    secondary: css`
        background: ${ob.color.surface};
        color: ${ob.color.brand};
        border: 1.5px solid ${ob.color.border};
        ${ob.hover} {
            &:hover:not(:disabled):not([aria-disabled='true']) {
                background: ${ob.color.lime50};
                border-color: ${ob.color.brand};
            }
        }
    `,
};

export const buttonStyles = ({ variant = 'primary' }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: ${ob.control};
    padding: 0 24px;
    border: 0;
    border-radius: ${ob.radius.pill};
    font-family: ${ob.font.body};
    font-size: 17px;
    font-weight: 600;
    line-height: 1.1;
    text-align: center;
    text-decoration: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition:
        background ${ob.motion.fast},
        transform ${ob.motion.fast},
        border-color ${ob.motion.fast};

    ${variants[variant]}

    i {
        font-size: 20px;
        line-height: 1;
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${ob.focusRing};
    }

    &:active:not(:disabled):not([aria-disabled='true']) {
        transform: scale(0.98);
    }

    &:disabled,
    &[aria-disabled='true'] {
        cursor: not-allowed;
        background: ${ob.color.disabledBg};
        color: ${ob.color.disabledText};
        border-color: transparent;
        box-shadow: none;
    }

    &[aria-busy='true']:disabled {
        background: ${variant === 'primary' ? ob.color.brand : ob.color.disabledBg};
        color: ${variant === 'primary' ? ob.color.surface : ob.color.disabledText};
        opacity: 0.85;
        cursor: progress;
    }

    ${ob.reducedMotion} {
        transition: none;
        &:active:not(:disabled) {
            transform: none;
        }
    }
`;

export const StyledButton = styled.button`
    ${buttonStyles}
`;

export const StyledAnchor = styled.a`
    ${buttonStyles}
`;

export const Spinner = styled.i`
    animation: ${spin} 0.8s linear infinite;

    ${ob.reducedMotion} {
        animation-duration: 2s;
    }
`;

const textTones = {
    default: css`
        color: ${ob.color.body};
        font-size: 15px;
        font-weight: 600;
    `,
    brand: css`
        color: ${ob.color.brand};
        font-size: 15px;
        font-weight: 700;
    `,
    muted: css`
        color: ${ob.color.muted};
        font-size: 15px;
        font-weight: 500;
        text-decoration: underline;
        text-underline-offset: 3px;
    `,
};

export const textButtonStyles = ({ tone = 'default' }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: ${ob.tap};
    padding: 0 12px;
    border: 0;
    border-radius: ${ob.radius.pill};
    background: transparent;
    font-family: ${ob.font.body};
    line-height: 1.2;
    text-decoration: none;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    ${textTones[tone]}

    i {
        font-size: 20px;
        line-height: 1;
    }

    ${ob.hover} {
        &:hover:not(:disabled) {
            text-decoration: underline;
            text-underline-offset: 3px;
        }
    }

    &:focus-visible {
        outline: none;
        box-shadow: ${ob.focusRing};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.55;
    }
`;

export const StyledTextButton = styled.button`
    ${textButtonStyles}
`;

export const StyledTextAnchor = styled.a`
    ${textButtonStyles}
`;
